/**
 * Compact time for the conversation sidebar.
 *
 * Only the first five minutes are relative. After that, today's conversations
 * show the actual time and older conversations show a calendar date.
 */
export function relativeTime(ts: number, nowTs = Date.now()): string {
  const elapsed = Math.max(0, nowTs - ts);
  if (elapsed < 60_000) return 'now';
  if (elapsed <= 5 * 60_000) return `${Math.floor(elapsed / 60_000)}m`;

  const date = new Date(ts);
  const now = new Date(nowTs);
  const sameDay = date.getFullYear() === now.getFullYear()
    && date.getMonth() === now.getMonth()
    && date.getDate() === now.getDate();
  if (sameDay) {
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  }

  const monthDay = `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
  return date.getFullYear() === now.getFullYear()
    ? monthDay
    : `${date.getFullYear()}/${monthDay}`;
}

/**
 * Formatted date-time for meeting cards and detail views.
 * Accepts a unix timestamp in **seconds** (not ms). Returns '' if null/0.
 * Output examples: "今天 14:30", "昨天 09:15", "周三 11:00", "6/1 14:30"
 */
export function formatDateTime(unix: number | null): string {
  if (!unix) return '';
  const d = new Date(unix * 1000);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - d.getTime()) / 86400000);
  const locale = getCurrentLocale();
  const time = d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  const relative = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  if (diffDays === 0) return `${relative.format(0, 'day')} ${time}`;
  if (diffDays === 1) return `${relative.format(-1, 'day')} ${time}`;
  if (diffDays < 7) {
    const weekday = new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(d);
    return `${weekday} ${time}`;
  }
  return `${new Intl.DateTimeFormat(locale, { month: 'numeric', day: 'numeric' }).format(d)} ${time}`;
}

/**
 * Localized relative time for logs/activity.
 */
export function relativeTimeCN(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60_000);
  const formatter = new Intl.RelativeTimeFormat(getCurrentLocale(), { numeric: 'auto' });
  if (mins < 1) return formatter.format(0, 'second');
  if (mins < 60) return formatter.format(-mins, 'minute');
  const hours = Math.floor(mins / 60);
  if (hours < 24) return formatter.format(-hours, 'hour');
  const days = Math.floor(hours / 24);
  return formatter.format(-days, 'day');
}
import { getCurrentLocale } from './i18n';
